import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck } from 'lucide-react';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <div className="border-b border-[#1A1A1A]">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <Link to="/settings" className="inline-flex items-center gap-2 text-muted-foreground hover:text-white transition-colors mb-4" data-testid="privacy-back-link">
            <ArrowLeft size={16} />
            Back to Settings
          </Link>
          <div className="flex items-center gap-3">
            <ShieldCheck size={28} className="text-gold" />
            <h1 className="font-serif text-3xl text-white" data-testid="privacy-title">Privacy Policy</h1>
          </div>
          <p className="text-muted-foreground mt-2">Last updated: February 2026</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12" data-testid="privacy-content">
        <div className="prose prose-invert prose-gold max-w-none space-y-8">

          <section>
            <p className="text-gray-400 leading-relaxed">
              Assagart ("we", "us") is a wealth tracking, risk management and advisor coordination platform.
              We take the privacy and security of your personal and asset data extremely seriously.
              This Privacy Policy explains what we collect, why we collect it, how it is protected, and the
              rights you have under the UK GDPR and the Data Protection Act 2018.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-white border-b border-[#262626] pb-2">1. Data we collect</h2>
            <p className="text-gray-400 leading-relaxed mb-3">
              We only collect the information needed to deliver the Service. This falls into three buckets:
            </p>
            <ul className="text-gray-400 list-disc list-outside ml-6 space-y-2">
              <li>
                <strong className="text-white">Account data</strong> - your name, email, password hash,
                role, and login activity.
              </li>
              <li>
                <strong className="text-white">Asset & risk data</strong> - items in your vault, valuations,
                insurance providers and policies, maintenance history, documents you upload.
              </li>
              <li>
                <strong className="text-white">Personal Profile (encrypted)</strong> - sensitive identifiers
                you optionally add to your profile such as National Insurance number, Tax ID, passport
                number, and date of birth.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif text-xl text-white border-b border-[#262626] pb-2">2. How sensitive identifiers are protected</h2>
            <p className="text-gray-400 leading-relaxed">
              Personal Profile fields (NI number, Tax ID, passport number, date of birth) are
              <strong className="text-white"> encrypted at rest</strong> using a server-side key that is never
              exposed to the application layer. They are never displayed in full in the UI - only the last
              four characters are shown - and they are never written to logs, analytics, or third-party tools.
              These fields are excluded from any AI processing.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-white border-b border-[#262626] pb-2">3. Why we use your data</h2>
            <ul className="text-gray-400 list-disc list-outside ml-6 space-y-2">
              <li>To run the Service (authentication, sessions, displaying your vault).</li>
              <li>To compute your <strong className="text-white">Portfolio Health Score</strong>, coverage
                gaps, concentration risk and renewal calendar.</li>
              <li>To send you renewal alerts and platform notifications.</li>
              <li>To process subscription payments via Stripe (we never see your full card details).</li>
              <li>To support your communications with assigned advisors and partners (with your consent).</li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif text-xl text-white border-b border-[#262626] pb-2">4. AI processing</h2>
            <p className="text-gray-400 leading-relaxed">
              When you use the AI Insights feature, only your <em>asset metadata</em> (category, valuation,
              coverage status, dates) is sent to the underlying language model - never your encrypted
              identifiers, never your contact details, and never any uploaded documents. You can ask us
              to disable AI processing at any time from Settings.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-white border-b border-[#262626] pb-2">5. Sharing your data</h2>
            <p className="text-gray-400 leading-relaxed mb-3">
              We do not sell your data. We share data only with:
            </p>
            <ul className="text-gray-400 list-disc list-outside ml-6 space-y-2">
              <li><strong className="text-white">Stripe</strong> - for processing subscription payments.</li>
              <li><strong className="text-white">Resend</strong> - for delivering transactional emails.</li>
              <li><strong className="text-white">Your assigned advisor</strong> - only if you have explicitly
                connected one and only the data items you have shared with them.</li>
              <li><strong className="text-white">Law enforcement</strong> - when legally compelled by a valid
                court order or statutory request.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif text-xl text-white border-b border-[#262626] pb-2">6. Where we store your data</h2>
            <p className="text-gray-400 leading-relaxed">
              All Assagart application data is stored within the European Economic Area on encrypted
              database clusters with daily off-site backups. Backups are retained for 30 days and then
              destroyed automatically.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-white border-b border-[#262626] pb-2">7. Your rights under UK GDPR</h2>
            <p className="text-gray-400 leading-relaxed mb-3">
              You have the right at any time to:
            </p>
            <ul className="text-gray-400 list-disc list-outside ml-6 space-y-2">
              <li><strong className="text-white">Access</strong> a complete export of your data - available
                in Settings → "Export My Data". This produces a JSON archive of every record we hold on you.</li>
              <li><strong className="text-white">Rectify</strong> inaccurate information by editing your
                Profile or contacting us.</li>
              <li><strong className="text-white">Erase</strong> your account and all associated data - 
                available in Settings → "Delete Account". This action is immediate and irreversible.</li>
              <li><strong className="text-white">Restrict or object</strong> to specific processing
                (e.g., disable AI features, marketing emails).</li>
              <li><strong className="text-white">Lodge a complaint</strong> with the UK Information
                Commissioner's Office (ICO) at <span className="text-gold">ico.org.uk</span>.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif text-xl text-white border-b border-[#262626] pb-2">8. Retention</h2>
            <p className="text-gray-400 leading-relaxed">
              We retain account and vault data for as long as your account is active. When you delete your
              account, your records are removed from production systems immediately and from encrypted
              backups within 30 days. Stripe and Resend may retain transactional records for the periods
              required by their own legal obligations.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-white border-b border-[#262626] pb-2">9. Cookies</h2>
            <p className="text-gray-400 leading-relaxed">
              We use a small number of strictly necessary cookies for authentication and session
              management, and self-hosted product analytics. We do not run third-party advertising or
              tracking pixels. See our <Link to="/cookies" className="text-gold hover:underline">Cookie Policy</Link>
              for the full list.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-white border-b border-[#262626] pb-2">10. Contact</h2>
            <p className="text-gray-400 leading-relaxed">
              For privacy questions, data access requests, or complaints, contact us at
              <span className="text-gold"> support@assagart.co.uk</span>. We aim to respond to all GDPR
              requests within 30 days. We are a small team and do not currently operate a dedicated Data
              Protection Officer; your request will be handled by a member of our senior team.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-white border-b border-[#262626] pb-2">11. Changes to this policy</h2>
            <p className="text-gray-400 leading-relaxed">
              We will notify you by email and via an in-app banner at least 14 days before any material
              change to this Privacy Policy takes effect.
            </p>
          </section>

        </div>
      </div>
    </div>
  );
}
